// Helix·CX — per-section structural skeleton (ported from
// docs/design/handoff_owner_shell/prototype/shell/skeleton.jsx). Renders the design-
// language scaffold for an IA section that hasn't been built yet, tagged
// with the source product it'll be filled from. Mounted at /skeleton/:id.
// Drawn with the template's parts: the heading block of dashboard/finance, the
// dashed coming-soon box of dashboard/analytics, Badge and Card through hx.
// Record: docs/testing/ui-migration/096-section-skeleton.md.

import { useParams } from "react-router-dom";
import Shell from "../components/Shell";
import { ShellIcon } from "../components/shell/icons";
import { HxCard, HxChip } from "../components/hx";
import type { Me } from "../App";

const PROD: Record<string, string> = {
  engage: "Omnichannel conversations",
  design: "VersaTalk · voice agent",
  intel: "VerSight · analytics",
  customers: "Customer graph",
  integrations: "Connected systems",
  admin: "Platform admin",
};

type Block =
  | { k: "kpis"; n: number; label: string }
  | { k: "table"; title?: string; eyebrow?: string; cols: string[]; rows: number }
  | { k: "cards"; n: number; cols: number; label: string }
  | { k: "ph"; label: string; sub?: string; h?: number }
  | { k: "cols2"; a: { label: string; sub?: string; h?: number }; b: { label: string; sub?: string; h?: number } }
  | { k: "listdetail"; listLabel: string; detailLabel: string; detailSub?: string }
  | { k: "workspace" };

interface Spec {
  group: string;
  layer: string;
  title: string;
  sub: string;
  blocks: Block[];
}

const SPEC: Record<string, Spec> = {
  tickets: {
    group: "Engage", layer: "engage", title: "Tickets", sub: "Same timeline, ticket-state lens",
    blocks: [
      { k: "kpis", n: 4, label: "open / SLA / escalated / resolved" },
      { k: "table", title: "Ticket queue", eyebrow: "a ticket is a state on the timeline", cols: ["Customer", "Status", "Node", "SLA", "Assignee", "Age"], rows: 8 },
    ],
  },
  flow: {
    group: "Design", layer: "design", title: "Flow graphs", sub: "The 6-option routing line — the keystone",
    blocks: [
      { k: "ph", label: "flow graph editor", sub: "drag nodes · branch · attach knowledge", h: 360 },
      { k: "ph", label: "node inspector", sub: "prompts · fields collected · routing · AI tags", h: 150 },
    ],
  },
  knowledge: {
    group: "Design", layer: "design", title: "Knowledge & SOPs", sub: "The library the action pane surfaces from",
    blocks: [{ k: "listdetail", listLabel: "SOP / guide list", detailLabel: "document reader", detailSub: "RMA process · freight guide · warranty terms" }],
  },
  products: {
    group: "Design", layer: "design", title: "Products", sub: "Catalog surfaced into conversations",
    blocks: [{ k: "cards", n: 6, cols: 3, label: "product (range hood, sink…)" }],
  },
  routing: {
    group: "Design", layer: "design", title: "Routing & escalation", sub: "State/region table + escalation rules",
    blocks: [
      { k: "table", title: "State / region → rep", eyebrow: "option 1 · sales", cols: ["State", "Region", "Primary rep", "Backup / manager"], rows: 6 },
      { k: "table", title: "Department routing", eyebrow: "options 2–6", cols: ["Option", "Department", "Route to", "Priority"], rows: 5 },
    ],
  },
  cx: {
    group: "Intelligence", layer: "intel", title: "Customer experience", sub: "CSAT, sentiment, resolution — by channel",
    blocks: [
      { k: "kpis", n: 4, label: "CSAT / sentiment / resolution / repeat" },
      { k: "cols2", a: { label: "sentiment over time", sub: "pos / neutral / neg area", h: 240 }, b: { label: "CSAT by branch", sub: "the 6 routing options", h: 240 } },
    ],
  },
  segments: {
    group: "Customers", layer: "customers", title: "Segments", sub: "Trade vs. homeowner, by region, by value",
    blocks: [{ k: "cards", n: 6, cols: 3, label: "segment" }],
  },
  messaging: {
    group: "Integrations", layer: "integrations", title: "Email & WhatsApp", sub: "Message providers feeding the timeline",
    blocks: [{ k: "cards", n: 4, cols: 2, label: "provider (email, WhatsApp…)" }],
  },
};

// The template's dashed coming-soon box (dashboard/analytics/page.tsx:70).
function Ph({ label, sub, h = 120 }: { label: string; sub?: string; h?: number }) {
  return (
    <div className="flex items-center justify-center overflow-hidden rounded-xl border border-dashed bg-card" style={{ minHeight: h }}>
      <div className="max-w-[92%] px-3 py-2 text-center font-mono text-muted-foreground text-xs leading-normal">
        {label}{sub && <span className="mt-0.5 block text-muted-foreground/70">{sub}</span>}
      </div>
    </div>
  );
}

// Grids collapse by breakpoint, as the template's do. Literal classes, so
// Tailwind generates them.
const GRID_COLS: Record<number, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-2 lg:grid-cols-3",
  4: "md:grid-cols-2 xl:grid-cols-4",
};

function BlockView({ b }: { b: Block }) {
  switch (b.k) {
    case "kpis":
    case "cards": {
      const cols = b.k === "kpis" ? b.n : b.cols;
      return (
        <div className={"grid grid-cols-1 gap-3 " + (GRID_COLS[cols] ?? "")}>
          {Array.from({ length: b.n }).map((_, i) => (
            <Ph key={i} label={b.label} sub={`#${i + 1}`} h={b.k === "kpis" ? 96 : 134} />
          ))}
        </div>
      );
    }
    case "table": {
      const widths = b.cols.map((_, i) => (i === 0 ? 150 : 70 + ((i * 37) % 60)));
      return (
        <HxCard
          className="overflow-hidden"
          style={{ padding: 0 }}
          title={b.title && (
            <span className="flex flex-col gap-0.5 px-4 pt-3">
              {b.eyebrow && <span className="font-normal text-muted-foreground text-xs">{b.eyebrow}</span>}
              <span>{b.title}</span>
            </span>
          )}
        >
          <div className="flex gap-3.5 px-4 py-3">
            {b.cols.map((_c, i) => <div className="h-2 rounded-sm bg-foreground/10" key={i} style={{ width: widths[i] }} />)}
          </div>
          {Array.from({ length: b.rows }).map((_, r) => (
            <div className="flex items-center gap-3.5 border-t px-4 py-3.5" key={r}>
              <div className="size-7 flex-none rounded-md bg-foreground/8" />
              {b.cols.map((_c, i) => <div className={"h-2 rounded-sm " + (i % 2 ? "bg-foreground/4" : "bg-foreground/8")} key={i} style={{ width: widths[i] }} />)}
            </div>
          ))}
        </HxCard>
      );
    }
    case "ph":
      return <Ph label={b.label} sub={b.sub} h={b.h} />;
    case "cols2":
      return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Ph label={b.a.label} sub={b.a.sub} h={b.a.h} />
          <Ph label={b.b.label} sub={b.b.sub} h={b.b.h} />
        </div>
      );
    case "listdetail":
      return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
          <Ph label={b.listLabel} h={420} />
          <Ph label={b.detailLabel} sub={b.detailSub} h={420} />
        </div>
      );
    case "workspace":
      return (
        <div className="grid min-h-[420px] grid-cols-1 gap-4 lg:grid-cols-[300px_minmax(0,1fr)_360px]">
          <Ph label="conversation list" sub="lens tabs + rows" h={460} />
          <Ph label="customer timeline" sub="never covered" h={460} />
          <Ph label="node-aware action pane" h={460} />
        </div>
      );
    default:
      return null;
  }
}

export default function SectionSkeleton({ me, onLoggedOut }: { me: Me; onLoggedOut: () => Promise<void> | void }) {
  const { id } = useParams<{ id: string }>();
  const s = id ? SPEC[id] : undefined;

  if (!s) {
    return (
      <Shell me={me} onLoggedOut={onLoggedOut} title="Section">
        <div className="work-inner">
          {/* The template's coming-soon page, with Empty's icon box and a Badge. */}
          <div className="rise flex min-h-[56vh] flex-col items-center justify-center gap-2 text-center">
            <div className="flex size-8 items-center justify-center rounded-md bg-muted text-foreground"><ShellIcon name="grid" size={24} /></div>
            <h2 className="m-0 font-semibold text-2xl">Mapped in the shell</h2>
            <p className="m-0 max-w-[440px] text-muted-foreground">This section is part of the new information architecture and will be built next.</p>
            <HxChip className="mt-2">building next</HxChip>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell me={me} onLoggedOut={onLoggedOut}>
      <div className="work-inner">
        <div className="mb-4 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
            <span className="text-foreground">{s.group}</span> <ShellIcon name="chevron" size={12} /> {s.title}
          </div>
          <h1 className="m-0 font-normal text-3xl tracking-tight">{s.title}</h1>
          <span className="text-muted-foreground text-sm">{s.sub}</span>
        </div>
        <div className="mb-4 flex items-center gap-2.5">
          <HxChip>○ skeleton · ready to fill</HxChip>
          <span className="text-muted-foreground text-xs">from <b className="font-semibold text-foreground">{PROD[s.layer]}</b></span>
        </div>
        <div className="flex flex-col gap-4">
          {s.blocks.map((b, i) => (
            <div className="rise" key={i} style={{ animationDelay: i * 50 + "ms" }}><BlockView b={b} /></div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
